import { makeAutoObservable } from "mobx";
import type { MapRef } from "react-map-gl/mapbox";
import type { Map as MapboxMap } from "mapbox-gl";
import type { RefObject } from "react";
import { AsyncStateStore } from "@/utils/AsyncStateStore";
import { overlay } from "@/endpoints/dryp/overlay";

const MARKERS_LAYER_ID = "markers-layer";
const MARKERS_SHADOW_LAYER_ID = "markers-layer-shadow";

const layerIdsFor = (overlayId: Dryp.OverlayId) => ({
	source: `overlay-source-${overlayId}`,
	fill: `overlay-${overlayId}-fill`,
	line: `overlay-${overlayId}-line`,
	point: `overlay-${overlayId}-point`,
});

/**
 * Lazy, click-driven overlay loader. The catalog (list of available overlays
 * for the user/org) is fetched once on attach. Each overlay's data is fetched
 * the first time the user shows it, with the *current* map bbox, then cached
 * forever (no re-fetch on pan/zoom — toggle off and on if the user needs a
 * different region).
 */
export interface OverlayHover {
	overlayId: Dryp.OverlayId;
	name: string;
	color: string;
	lng: number;
	lat: number;
}

export class OverlaysStore {
	catalog = new AsyncStateStore(overlay.getAll);
	visible = new Set<Dryp.OverlayId>();
	hover: OverlayHover | null = null;

	private mapRef: RefObject<MapRef | null>;
	private dataApis = new Map<Dryp.OverlayId, AsyncStateStore<typeof overlay.getOverlay>>();
	private wiredHover = new Set<Dryp.OverlayId>();
	private styleLoadUnsub?: () => void;

	constructor(mapRef: RefObject<MapRef | null>) {
		this.mapRef = mapRef;
		makeAutoObservable(this, { mapRef: false } as never);
	}

	setHover = (overlayHover: OverlayHover) => {
		this.hover = overlayHover;
	};

	clearHover = (overlayId?: Dryp.OverlayId) => {
		// Optional id guard: ignore leave events from a layer we're no longer hovering
		// (prevents flicker when moving between line and fill of the same overlay).
		if (overlayId != null && this.hover?.overlayId !== overlayId) return;
		this.hover = null;
	};

	attach = async () => {
		await this.catalog.fetchIfEmpty();
		this.bindStyleReload();
	};

	private bindStyleReload = () => {
		const map = this.getMap();
		if (!map) return;
		this.styleLoadUnsub?.();
		const onStyleLoad = () => {
			// Mapbox wipes custom sources/layers on background change. Re-add
			// each currently visible overlay so it survives the swap.
			this.wiredHover.clear();
			for (const overlayId of this.visible) {
				const cached = this.dataApis.get(overlayId)?.data as GeoJSON.FeatureCollection | undefined;
				if (!cached) continue;
				this.ensureLayers(map, overlayId, cached);
				this.setVisibility(map, overlayId, true);
			}
		};
		map.on("style.load", onStyleLoad);
		this.styleLoadUnsub = () => map.off("style.load", onStyleLoad);
	};

	toggle = async (overlayId: Dryp.OverlayId) => {
		if (this.visible.has(overlayId)) {
			this.hide(overlayId);
		} else {
			await this.show(overlayId);
		}
	};

	isVisible = (overlayId: Dryp.OverlayId): boolean => this.visible.has(overlayId);

	private show = async (overlayId: Dryp.OverlayId) => {
		this.visible.add(overlayId);

		const map = this.getMap();
		if (!map) return;

		const dataApi = this.dataApiFor(overlayId);
		const bbox = this.currentBbox(map);
		await dataApi.fetchIfEmpty(overlayId, bbox);
		if (!this.visible.has(overlayId)) return;

		const data = dataApi.data;
		if (!data) return;

		this.ensureLayers(map, overlayId, data as GeoJSON.FeatureCollection);
		this.setVisibility(map, overlayId, true);
	};

	private hide = (overlayId: Dryp.OverlayId) => {
		const map = this.getMap();
		if (!map) return;
		this.setVisibility(map, overlayId, false);
		this.visible.delete(overlayId);
	};

	private dataApiFor(overlayId: Dryp.OverlayId): AsyncStateStore<typeof overlay.getOverlay> {
		let api = this.dataApis.get(overlayId);
		if (!api) {
			api = new AsyncStateStore(overlay.getOverlay);
			this.dataApis.set(overlayId, api);
		}
		return api;
	}

	private currentBbox(map: MapboxMap): string {
		const bounds = map.getBounds();
		if (!bounds) return "-180,-90,180,90";
		return `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
	}

	private ensureLayers(map: MapboxMap, overlayId: Dryp.OverlayId, data: GeoJSON.FeatureCollection) {
		const ids = layerIdsFor(overlayId);
		const overlayDetails = (this.catalog.data || []).find(item => item.overlayId === overlayId);
		const color = overlayDetails?.color || "#3b82f6";

		const existingSource = map.getSource(ids.source);
		if (existingSource) {
			(existingSource as unknown as { setData(data: GeoJSON.FeatureCollection): void }).setData(data);
		} else {
			map.addSource(ids.source, { type: "geojson", data });
		}

		// Insert overlay layers below the markers so structures stay visible on top.
		const beforeId = map.getLayer(MARKERS_SHADOW_LAYER_ID)
			? MARKERS_SHADOW_LAYER_ID
			: map.getLayer(MARKERS_LAYER_ID)
				? MARKERS_LAYER_ID
				: undefined;

		if (!map.getLayer(ids.fill)) {
			map.addLayer(
				{
					id: ids.fill,
					type: "fill",
					source: ids.source,
					filter: ["==", ["geometry-type"], "Polygon"],
					paint: { "fill-opacity": 0.2, "fill-color": color },
				},
				beforeId,
			);
		}

		if (!map.getLayer(ids.line)) {
			map.addLayer(
				{
					id: ids.line,
					type: "line",
					source: ids.source,
					filter: ["in", ["geometry-type"], ["literal", ["LineString", "MultiLineString"]]],
					paint: { "line-width": 2, "line-color": color },
				},
				beforeId,
			);
		}

		if (!map.getLayer(ids.point)) {
			map.addLayer(
				{
					id: ids.point,
					type: "circle",
					source: ids.source,
					filter: ["==", ["geometry-type"], "Point"],
					paint: {
						"circle-radius": 5,
						"circle-color": color,
						"circle-stroke-width": 1,
						"circle-stroke-color": "#ffffff",
					},
				},
				beforeId,
			);
		}

		// Wire hover once per overlay (layers persist after creation).
		if (!this.wiredHover.has(overlayId)) {
			this.wireHover(map, overlayId, overlayDetails?.name ?? "Overlay", color);
			this.wiredHover.add(overlayId);
		}
	}

	private wireHover(map: MapboxMap, overlayId: Dryp.OverlayId, name: string, color: string) {
		const ids = layerIdsFor(overlayId);
		const layers = [ids.fill, ids.line, ids.point];

		const onMove = (event: { lngLat: { lng: number; lat: number } }) => {
			this.setHover({ overlayId, name, color, lng: event.lngLat.lng, lat: event.lngLat.lat });
		};
		const onEnter = () => {
			map.getCanvas().style.cursor = "pointer";
		};
		const onLeave = () => {
			map.getCanvas().style.cursor = "";
			this.clearHover(overlayId);
		};

		for (const layerId of layers) {
			map.on("mouseenter", layerId, onEnter);
			map.on("mousemove", layerId, onMove);
			map.on("mouseleave", layerId, onLeave);
		}
	}

	private setVisibility(map: MapboxMap, overlayId: Dryp.OverlayId, visible: boolean) {
		const ids = layerIdsFor(overlayId);
		const value = visible ? "visible" : "none";
		if (map.getLayer(ids.fill)) map.setLayoutProperty(ids.fill, "visibility", value);
		if (map.getLayer(ids.line)) map.setLayoutProperty(ids.line, "visibility", value);
		if (map.getLayer(ids.point)) map.setLayoutProperty(ids.point, "visibility", value);
	}

	private getMap(): MapboxMap | null {
		const instance = this.mapRef.current;
		return instance ? (instance.getMap() as MapboxMap) : null;
	}
}
